import moment from "moment";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ApiTag, ApiTaskKanban, ApiUserMinimal } from "src/api/types";
import Avatar from "src/components/Avatar";
import Card from "src/components/Card";
import DropdownButton from "src/components/DropdownButton";
import Tag from "src/components/Tag";
import UserAvatars from "src/components/UserAvatars";
import withTranslation, {
	Translation,
} from "src/components/hoc/withTranslation";
import PublishTaskButton from "src/components/tasks/PublishTaskButton";
import TaskMatchProfile from "src/components/tasks/TaskMatchProfile";
import TaskMatchProfiles from "src/components/tasks/TaskMatchProfiles";
import TaskTypeIndicator from "src/components/tasks/TaskTypeIndicator";
import useLimits from "src/hooks/api/services/auth/useLimits";
import useTeams from "src/hooks/api/services/users/useTeams";
import useUsers from "src/hooks/api/services/users/useUsers";
import useTaskForm from "src/hooks/api/tasks/useTaskForm";

interface TaskKanbanCardProps extends Translation {
	task: ApiTaskKanban;
	onClick?: (e: any) => void;
}

const TaskKanbanCard = ({ t, task, onClick }: TaskKanbanCardProps) => {
	const { actions } = useTaskForm();
	const { limits } = useLimits();
	const [status, setStatus] = useState<"idle" | "deleting" | "duplicating">(
		"idle"
	);
	const navigate = useNavigate();
	const { users } = useUsers();
	const { teams } = useTeams();
	const creator = task?.id
		? users.find((user) => user.id === task?.links?.creator_id)
		: null;
	const match = task?.matches ? task.matches.users[0] : null;
	const assignees = users.filter((u) =>
		(task.links.assignees || []).includes(u.id)
	);

	let teamSlugs = assignees.map((user: ApiUserMinimal) => user.teams).flat();

	if (creator && creator.teams && !assignees.length) {
		teamSlugs = [...teamSlugs, ...creator.teams];
	}

	const linkedTeams = teams.filter((team) =>
		(teamSlugs || []).includes(team.slug)
	);

	return (
		<Card
			{...{ onClick }}
			contentClassName=" p-4"
			className="cursor-pointer shadow"
		>
			<div className="flex items-center justify-between mb-2">
				<TaskTypeIndicator type={task.type} />
				<div className="flex items-center gap-2">
					{!!task.unread_messages && (
						<div className="text-red flex items-center gap-1">
							<i className="fas fa-comments"></i>
							<strong>{task.unread_messages}</strong>
						</div>
					)}
					<div className="">
						<DropdownButton
							menu={{
								position: "bottom-right",
							}}
							button={{
								tabIndex: "-1",
								children: <i className="far fa-ellipsis-v " />,
								type: "transparent",
								iconOnly: true,
								className:
									"hover:bg-gray p-0 w-6 h-6 rounded-sm",
							}}
						>
							{task.type === "PUBLISHED" && (
								<>
									<DropdownButton.Item
										icon="far fa-retweet"
										onClick={() =>
											navigate(
												`/tasks/create?repeat=${task.hid}`
											)
										}
									>
										{t("repeat")}
									</DropdownButton.Item>
								</>
							)}
							{task.type !== "PUBLISHED" && (
								<>
									<DropdownButton.Item
										icon="far fa-eye"
										onClick={() =>
											navigate(
												`/tasks/${task.hid}/editor`
											)
										}
									>
										{t("preview")}
									</DropdownButton.Item>
									<DropdownButton.Item
										loading={status === "duplicating"}
										icon="far fa-copy"
										onClick={(close) => {
											setStatus("duplicating");
											actions
												.duplicate(task.id)
												.then(() => {
													setStatus("idle");
													close();
												});
										}}
									>
										{t("duplicate")}
									</DropdownButton.Item>
									{limits?.published_tasks?.is_available && (
										<PublishTaskButton
											id={task.hid}
											variant="dropdown-button"
										/>
									)}
									<DropdownButton.Item
										loading={status === "deleting"}
										icon="far fa-archive"
										onClick={() => {
											setStatus("deleting");
											actions
												.archive(task.id)
												.then(() => {
													setStatus("idle");
												});
										}}
									>
										{t("archive")}
									</DropdownButton.Item>
								</>
							)}
						</DropdownButton>
					</div>
				</div>
			</div>
			<p className="font-regular">{task.title}</p>

			{/* {!!assignees.length && (
				<div className="mt-4">
					<UserAvatars
						users={assignees}
						avatar={{ size: "xsmall" }}
					/>
				</div>
			)} */}

			{(!!task.tags.length ||
				(!!linkedTeams.length && task.type === "INTERNAL")) && (
				<div className="flex flex-wrap gap-1 mt-4">
					{linkedTeams.map((team) => (
						<Tag
							custom={team.color}
							size="xsmall"
							key={`${team.id}-${task.hid}`}
						>
							{team.name}
						</Tag>
					))}

					{task.tags.map((tag: ApiTag) => (
						<Tag
							theme="dark"
							size="xsmall"
							key={`${tag.tag}-${task.hid}`}
						>
							{tag.tag}
						</Tag>
					))}
				</div>
			)}

			<div className="flex items-center justify-between mt-4">
				{task.type === "INTERNAL" && (
					<>
						{task.end_date ? (
							<small className="text-red">
								{moment(task.end_date).format("DD-MM-YYYY")}
							</small>
						) : (
							<small className="text-placeholder">
								{moment(task.created_at).format("DD-MM-YYYY")}
							</small>
						)}
					</>
				)}
				{match && task?.matches?.count === 1 && (
					<TaskMatchProfile
						avatar={{
							size: "xsmall",
						}}
						user={match}
					/>
				)}

				{task?.matches?.count && task?.matches?.count > 1 && (
					<TaskMatchProfiles users={task?.matches?.users || []} />
				)}

				{task.registrations && (
					<UserAvatars
						users={task.registrations.users}
						avatar={{
							size: "xsmall",
						}}
						overlay={
							<div className="absolute inset-0 bg-primary backdrop-blur-sm rounded-md flex justify-center items-center text-white">
								<strong>{task.registrations.count}</strong>
							</div>
						}
					/>
				)}

				{!!assignees.length ? (
					<UserAvatars
						users={assignees}
						avatar={{ size: "xsmall" }}
					/>
				) : creator ? (
					<Avatar.User
						user={creator}
						avatar={{
							size: "xsmall",
						}}
						options={{ iconOnly: true }}
					/>
				) : (
					false
				)}
			</div>
		</Card>
	);
};

TaskKanbanCard.locale = {
	nl: {
		preview: "Preview",
		publish: "Uitbesteden",
		archive: "Archiveren",
		duplicate: "Dupliceren",
		repeat: "Opnieuw uitbesteden",
	},
	en: {
		preview: "Preview",
		publish: "Outsource",
		archive: "Archive",
		duplicate: "Duplicate",
		repeat: "Outsource again",
	},
};

export default withTranslation(TaskKanbanCard);
